import { useSelector } from "react-redux";

import { FBPageWithInsta } from "../../../../api/social-account-connect/instagram";
import { Modal } from "../../../../components/modal/modal";
import { Button, FormSize } from "../../../../ui/ecosystems/forms";
import { Alert, AlertType } from "../../../../ui/alert";

import { InstagramAccountList } from "../instagram-account-list";
import { InstagramConnectInstructions } from "../instagram-connect-instructions";
import { NeedHelpButton } from "../need-help-button";
import {
  getAccountsFx,
  getAccountsAnotherWayFx,
  addInstagramAccountFx,
  reconnectInstagramAccountFx,
} from "./model";
import { ConnectAnotherWayLink } from "../connect-another-way-prompt/connect-another-way-link";
import { getConnectButtonStateFactory } from "../connect-button";
import { PlatformTypes } from "../../../post";
import {
  publishNoticeText,
  selectSocialConnectInstagramState,
} from "../../redux-model";

interface InstagramConnectDialogProps {
  show: boolean;
  onHide(): void;
  onBack(): void;
  isAlreadyAdded(acc: FBPageWithInsta): boolean;
  /**
   * SKE-4716. The id of the already-added Sked account that has failed a
   * publish with a `needs_reconnect` error, or undefined.
   *
   * One prop rather than a boolean predicate plus a separate lookup, because
   * the button and the request it fires must never disagree about WHICH
   * account is being reconnected. Such an account was rendering as an inert
   * "Connected" label with nothing to click.
   */
  reconnectAccountId(acc: FBPageWithInsta): string | undefined;
}

export function InstagramConnectDialog({
  show,
  onHide,
  onBack,
  isAlreadyAdded,
  reconnectAccountId,
}: InstagramConnectDialogProps) {
  const { data, gettingData, connectingAccountPageId, publishNotice } =
    useSelector(selectSocialConnectInstagramState);

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton onHide={onHide}>
        <Modal.Title>Connect Instagram Business account</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Always mounted, so a screen reader announces the text when it appears. */}
        <div role="status" aria-live="polite">
          {publishNotice ? (
            <div
              className="mb-4"
              data-testid="instagram-connect-publish-notice"
            >
              <Alert type={AlertType.warning}>
                {publishNoticeText(publishNotice)}
              </Alert>
            </div>
          ) : null}
        </div>
        {data && data.pages.length > 0 ? (
          <>
            <InstagramAccountList
              accounts={data.pages}
              onConnect={(page) => {
                if (!data) {
                  return;
                }
                /*
                 * An account the admin already has cannot go through
                 * `addInstagramAccountFx`: that posts to `/accounts/instagram`,
                 * which rejects it as a duplicate. The in-place upgrade endpoint
                 * is the route this picker uses to clear the reconnect flag.
                 */
                const accountId = reconnectAccountId(page);
                if (accountId) {
                  reconnectInstagramAccountFx({
                    accountId,
                    session: data.session,
                    page,
                  });
                } else {
                  addInstagramAccountFx({ session: data.session, page });
                }
              }}
              getConnectButtonState={getConnectButtonStateFactory({
                isConnected: isAlreadyAdded,
                needsReconnect: (page) => Boolean(reconnectAccountId(page)),
                isConnecting: (page) =>
                  page.page.id === connectingAccountPageId,
                isDisabled: () =>
                  Boolean(connectingAccountPageId) || gettingData,
              })}
            />
            <ConnectAnotherWayLink
              session={data.session}
              disabled={Boolean(connectingAccountPageId) || gettingData}
              onClick={() => getAccountsAnotherWayFx(data.session)}
            />
          </>
        ) : (
          <InstagramConnectInstructions
            ButtonProps={{
              disabled: gettingData,
              onClick: getAccountsFx,
            }}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button text size={FormSize.large} onClick={onBack}>
          Back
        </Button>
        <NeedHelpButton
          platform={PlatformTypes.instagram}
          chatQuery="accounts"
        />
      </Modal.Footer>
    </Modal>
  );
}
